import React, { createContext, useContext, useState, useEffect } from 'react';
import jwt_decode from 'jwt-decode';
import api from '../services/api';
import siteConfig from '../config/siteConfig';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [site, setSite] = useState(siteConfig);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const response = await api.get('/site/config');
        if (response.data?.site) {
          setSite(response.data.site);
        }
      } catch (error) {

      }

      // Check if token exists in localStorage
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // Check if token is expired
          const decodedToken = jwt_decode(token);
          const currentTime = Date.now() / 1000;

          if (decodedToken.exp > currentTime) {
            // Token is valid and will be automatically included in API requests
            setUser(decodedToken);
            setIsAuthenticated(true);
          } else {
            // Token expired, remove it
            localStorage.removeItem('token');
          }
        } catch (error) {
          // Invalid token
          localStorage.removeItem('token');
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      setLoading(true);

      // Make the API request to the backend
      const response = await api.post('/auth/login', {
        email: email.trim(),
        password
      });

      // Extract token and user data from response
      const { token, user, site } = response.data;

      // Store token in localStorage
      localStorage.setItem('token', token);

      // Update auth state
      setUser(user);
      if (site) {
        setSite(site);
      }

      setIsAuthenticated(true);

      return true;
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.message || 'Login failed. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    // Remove token from localStorage
    localStorage.removeItem('token');

    // Auth header will be automatically removed for future requests

    // Reset state
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ user, site, isAuthenticated, loading, error, login, logout }}>
      {children}
    </AuthContext.Provider>);

};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
